import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import Navbar from '../components/common/Navbar';
import PlanSelectionModal from '../components/modals/PlanSelectionModal';
import { fetchSubscriptionPlans, initializeGiftPayment, verifyGiftPayment, createGiftPaypalOrder, captureGiftPaypalOrder } from '../utils/api';
import { isValidEmail } from '../utils/validators';
import useCountry from '../hooks/useCountry';
import { PayPalButtons } from '@paypal/react-paypal-js';
import '../styles/components/gift.css';

const Gift = () => {
    const [email, setEmail] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [plans, setPlans] = useState([]);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successData, setSuccessData] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [activeTab, setActiveTab] = useState('paystack');

    const emailInputRef = useRef(null);

    const { country, loading: countryLoading } = useCountry();

    // Hard-coded Rand prices (same as PlanSelectionModal)
    const randPrices = {
        1: 100,
        2: 370
    };

    useEffect(() => {
        loadPlans();
        checkPaymentCallback();
        // Auto-focus email input
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (!countryLoading && country) {
            if (country === 'ZA') {
                setActiveTab('paystack');
            } else {
                setActiveTab('paypal');
            }
        }
    }, [country, countryLoading]);

    const loadPlans = async () => {
        try {
            const plansData = await fetchSubscriptionPlans();
            setPlans(plansData);
        } catch (err) {
            setError('Failed to load subscription plans.');
        }
    };

    const checkPaymentCallback = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const reference = urlParams.get('reference') || urlParams.get('trxref');

        // Check for stored data from before redirect
        const storedEmail = sessionStorage.getItem('giftEmail');
        const storedPlanId = sessionStorage.getItem('giftPlanId');

        if (reference && storedEmail && storedPlanId) {
            setVerifying(true);
            try {
                const result = await verifyGiftPayment({
                    reference,
                    email: storedEmail,
                    planId: storedPlanId
                });

                if (result.status === 'success') {
                    setSuccessData({
                        code: result.activationCode,
                        planName: result.planName,
                        email: storedEmail
                    });
                    // Clear session storage
                    sessionStorage.removeItem('giftEmail');
                    sessionStorage.removeItem('giftPlanId');
                } else {
                    setError(result.message || 'Payment verification failed.');
                }
            } catch (err) {
                setError('An error occurred while verifying payment.');
            } finally {
                setVerifying(false);
                // Clean URL
                window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
            }
        }
    };

    const handlePlanSelect = (planId, planName) => {
        const plan = plans.find(p => p.id === planId);
        setSelectedPlan(plan);
        setShowPlanModal(false);
    };

    const getPriceDisplay = (plan) => {
        if (!plan) return '';
        if (activeTab === 'paystack') {
            return `R${randPrices[plan.id]?.toFixed(2) || '0.00'}`;
        } else {
            return `$${parseFloat(plan.Price).toFixed(2)}`;
        }
    };

    const handlePurchase = async () => {
        if (!email || !isValidEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!selectedPlan) {
            setError('Please select a plan.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const callbackUrl = `${window.location.origin}/#/gift`;

            // Determine amount and currency
            let amount;
            let currency;

            if (activeTab === 'paystack') {
                amount = randPrices[selectedPlan.id];
                currency = 'ZAR';
            } else {
                amount = parseFloat(selectedPlan.Price);
                currency = 'USD';
            }

            const result = await initializeGiftPayment({
                email,
                planId: selectedPlan.id,
                amount,
                currency,
                callbackUrl
            });

            if (result.status && result.data && result.data.authorization_url) {
                // Store data for verification after redirect
                sessionStorage.setItem('giftEmail', email);
                sessionStorage.setItem('giftPlanId', selectedPlan.id);

                // Redirect to Paystack
                window.location.href = result.data.authorization_url;
            } else {
                setError('Failed to initialize payment.');
            }
        } catch (err) {
            setError('An error occurred while initializing payment.');
        } finally {
            setLoading(false);
        }
    };

    const createPayPalOrder = async () => {
        if (!email || !isValidEmail(email)) {
            const msg = 'Please enter a valid email address.';
            setError(msg);
            throw new Error(msg);
        }
        if (!selectedPlan) {
            const msg = 'Please select a plan.';
            setError(msg);
            throw new Error(msg);
        }

        try {
            const amount = parseFloat(selectedPlan.Price);
            const result = await createGiftPaypalOrder({
                amount,
                planId: selectedPlan.id,
                email
            });

            if (result.id) {
                return result.id;
            } else {
                console.error('PayPal order creation failed:', result);
                throw new Error(result.error || 'Failed to create PayPal order');
            }
        } catch (err) {
            console.error('PayPal create order error:', err);
            setError(err.message || 'Failed to create PayPal order. Please try again.');
            throw err;
        }
    };

    const onPayPalApprove = async (data) => {
        setLoading(true);
        try {
            const result = await captureGiftPaypalOrder({
                orderID: data.orderID,
                planId: selectedPlan.id,
                email
            });

            if (result.status === 'success') {
                setSuccessData({
                    code: result.activationCode,
                    planName: result.planName,
                    email: email
                });
            } else {
                setError('Payment captured but verification failed. Please contact support.');
            }
        } catch (err) {
            console.error('PayPal capture error:', err);
            setError('An error occurred while processing the payment.');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <>
                <Navbar />
                <div className="gift-container">
                    <div className="text-center text-white">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Verifying payment...</p>
                    </div>
                </div>
            </>
        );
    }

    if (successData) {
        return (
            <>
                <Navbar />
                <div className="gift-container">
                    <div className="gift-card">
                        <div className="card-body text-center">
                            <div className="mb-4">
                                <i className="fas fa-gift fa-4x text-primary mb-3"></i>
                                <h2 className="text-success">Gift Purchased Successfully!</h2>
                            </div>

                            <p className="lead mb-4 text-white">
                                You have successfully purchased a <strong>{successData.planName}</strong> plan for <strong>{successData.email}</strong>.
                            </p>

                            <div className="success-code-box">
                                <p className="text-muted mb-2">Activation Code:</p>
                                <h1 className="success-code display-4">
                                    {successData.code}
                                </h1>
                            </div>

                            <p className="text-muted mb-4">
                                This code has also been emailed to the recipient.
                            </p>

                            <Button className="gift-btn w-100" onClick={() => {
                                setSuccessData(null);
                                setEmail('');
                                setSelectedPlan(null);
                            }}>
                                Send Another Gift
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="gift-container">
                <div className="gift-card">
                    {/* <div className="card-header text-center christmas-header">
                        <h2 className="text-white">Give the gift of security 🎁.</h2>
                        <p className="text-white mb-0">Purchase a PhēnixLoc subscription for someone special!</p>
                    </div> */}
                    <div className="card-header text-center">
                        <h2>Give the Gift of Security</h2>
                        <p className="text-muted mb-0">Purchase a PhēnixLoc subscription for someone</p> special 🎁.
                    </div>
                    <div className="card-body">
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label className="gift-form-label">Recipient's Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter recipient's email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    size="lg"
                                    className="gift-input"
                                    ref={emailInputRef}
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="gift-form-label">Select Plan</Form.Label>
                                <div
                                    className="plan-selector d-flex justify-content-between align-items-center cursor-pointer"
                                    onClick={() => setShowPlanModal(true)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {selectedPlan ? (
                                        <div>
                                            <h5 className="mb-0">{selectedPlan.Name}</h5>
                                            <small className="text-muted">{selectedPlan.Description}</small>
                                        </div>
                                    ) : (
                                        <span className="text-muted">Choose a subscription plan...</span>
                                    )}
                                    <i className="fas fa-chevron-right text-muted"></i>
                                </div>
                            </Form.Group>

                            {selectedPlan && (
                                <div className="price-summary mb-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>Total to pay:</span>
                                        <strong className="h4 mb-0">
                                            {countryLoading ? <Spinner size="sm" animation="border" /> : getPriceDisplay(selectedPlan)}
                                        </strong>
                                    </div>
                                </div>
                            )}

                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="mb-4"
                                fill
                            >
                                <Tab
                                    eventKey="paystack"
                                    title={
                                        <>
                                            <i className="fas fa-credit-card me-2"></i>
                                            Paystack {country === 'ZA' && <span className="badge bg-success ms-1">Recommended</span>}
                                        </>
                                    }
                                >
                                    <div className="p-3 text-white">
                                        <p className="mb-4 text-white">
                                            Pay securely using Paystack. You will be redirected to Paystack's payment page.
                                        </p>
                                        <Button
                                            className="gift-btn w-100"
                                            onClick={handlePurchase}
                                            disabled={loading || countryLoading}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Processing...
                                                </>
                                            ) : (
                                                'Purchase Gift'
                                            )}
                                        </Button>
                                    </div>
                                </Tab>
                                <Tab
                                    eventKey="paypal"
                                    title={
                                        <>
                                            <i className="fab fa-paypal me-2"></i>
                                            PayPal {country !== 'ZA' && country !== 'UNKNOWN' && <span className="badge bg-primary ms-1">Recommended</span>}
                                        </>
                                    }
                                >
                                    <div className="p-3">
                                        <p className="mb-4 text-white">
                                            Pay securely using PayPal. You can use your PayPal account or a credit card.
                                        </p>
                                        {loading ? (
                                            <div className="text-center py-3 text-white">
                                                <Spinner animation="border" variant="primary" />
                                                <p className="mt-2">Processing payment...</p>
                                            </div>
                                        ) : (
                                            <PayPalButtons
                                                style={{
                                                    color: 'blue',
                                                    shape: 'rect',
                                                    label: 'pay',
                                                    height: 50,
                                                }}
                                                createOrder={createPayPalOrder}
                                                onApprove={onPayPalApprove}
                                                onError={(err) => {
                                                    console.error('PayPal Error:', err);
                                                    setError('An error occurred with PayPal. Please try again.');
                                                }}
                                            />
                                        )}
                                    </div>
                                </Tab>
                            </Tabs>
                        </Form>
                    </div>
                </div>
            </div>

            <PlanSelectionModal
                isOpen={showPlanModal}
                onClose={() => setShowPlanModal(false)}
                plans={plans}
                onSelectPlan={handlePlanSelect}
                selectedPlanId={selectedPlan?.id}
                country={country} // Pass country to modal to avoid double fetch
            />
        </>
    );
};

export default Gift;
